import { useState, useMemo } from 'react';
import { Eye, Edit3, Save, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MarkdownRenderer } from '@/components/chat/MarkdownRenderer';
import { cn, debounce } from '@/lib/utils';

interface MarkdownEditorProps {
  content: string;
  onChange: (content: string) => void;
  onSave?: () => void;
  isSaving?: boolean;
  className?: string;
  placeholder?: string;
}

export function MarkdownEditor({
  content,
  onChange,
  onSave,
  isSaving,
  className,
  placeholder = 'Start writing...',
}: MarkdownEditorProps) {
  const [activeTab, setActiveTab] = useState<'edit' | 'preview' | 'split'>('split');
  const [localContent, setLocalContent] = useState(content);
  const [hasChanges, setHasChanges] = useState(false);

  // Debounced onChange handler
  const debouncedOnChange = useMemo(
    () => debounce((value: string) => {
      onChange(value);
    }, 500),
    [onChange]
  );

  const handleChange = (value: string) => {
    setLocalContent(value);
    setHasChanges(value !== content);
    debouncedOnChange(value);
  };

  const handleReset = () => {
    setLocalContent(content);
    setHasChanges(false);
  };

  const handleSave = () => {
    if (onSave) {
      onSave();
      setHasChanges(false);
    }
  };

  return (
    <div className={cn('flex h-full flex-col', className)}>
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b px-4 py-2">
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as typeof activeTab)}
        >
          <TabsList className="h-8">
            <TabsTrigger value="edit" className="h-6 px-2 text-xs">
              <Edit3 className="mr-1 h-3 w-3" />
              Edit
            </TabsTrigger>
            <TabsTrigger value="preview" className="h-6 px-2 text-xs">
              <Eye className="mr-1 h-3 w-3" />
              Preview
            </TabsTrigger>
            <TabsTrigger value="split" className="h-6 px-2 text-xs">
              Split
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex items-center gap-2">
          {hasChanges && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                className="h-7"
              >
                <RotateCcw className="mr-1 h-3 w-3" />
                Reset
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                loading={isSaving}
                className="h-7"
              >
                <Save className="mr-1 h-3 w-3" />
                Save
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Editor Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'edit' && (
          <Textarea
            value={localContent}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={placeholder}
            className="h-full resize-none rounded-none border-0 font-mono text-sm focus-visible:ring-0"
          />
        )}

        {activeTab === 'preview' && (
          <ScrollArea className="h-full">
            <div className="p-4">
              <MarkdownRenderer content={localContent || '*No content yet*'} />
            </div>
          </ScrollArea>
        )}

        {activeTab === 'split' && (
          <div className="grid h-full grid-cols-2 divide-x">
            <Textarea
              value={localContent}
              onChange={(e) => handleChange(e.target.value)}
              placeholder={placeholder}
              className="h-full resize-none rounded-none border-0 font-mono text-sm focus-visible:ring-0"
            />
            <ScrollArea className="h-full">
              <div className="p-4">
                <MarkdownRenderer content={localContent || '*No content yet*'} />
              </div>
            </ScrollArea>
          </div>
        )}
      </div>
    </div>
  );
}
